import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDeleteLeft } from "@fortawesome/free-solid-svg-icons";
import { CallStatusType } from "../../../configs";
import { ActionAudioCall, useAppDispatch } from "../../../core";
import { AudioConferenceCall, AudioConferenceCalls } from "../../../models";
import { SendOutboundDtmfRequestModel, ThreeWayCallReDialRequest } from "../../../models/api/Call";
import { formatUSPhoneNumber, isValidUSPhoneNumber } from "../../../utils/phoneNumberFormatting";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callSid?: string | null;
  audioConferenceCalls: AudioConferenceCalls;
  onCallConferenceParticipant: (value: ThreeWayCallReDialRequest) => Promise<any>;
  onSendOutboundDtmf: (value: SendOutboundDtmfRequestModel) => Promise<any>;
};

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const completedStatuses = new Set<CallStatusType>([
  CallStatusType.OutboundCallCompleted,
  CallStatusType.OutboundCallNotAnswered,
  CallStatusType.OutboundCallNotReceived,
]);

function normalizeUSPhoneNumber(phone: string): string {
  const digitsOnly = phone.replace(/\D/g, "");

  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return digitsOnly;
  }

  if (digitsOnly.length === 10) {
    return `1${digitsOnly}`;
  }

  throw new Error("Invalid US phone number format");
}

function getCallStatusLabel(current?: CallStatusType, prev?: CallStatusType): string {
  if (!current) return "Waiting";

  switch (current) {
    case CallStatusType.OutboundCallInitiated:
      return "Initiated";
    case CallStatusType.OutboundCallRinging:
      return "Ringing";
    case CallStatusType.OutboundCallReceived:
      return "In-call";
    case CallStatusType.OutboundCallCompleted:
      if (prev === CallStatusType.OutboundCallReceived) return "Hung Up";
      if (prev === CallStatusType.OutboundCallNotAnswered) return "Not Answered";
      return "Completed";
    case CallStatusType.OutboundCallNotAnswered:
      return "Not Answered";
    case CallStatusType.OutboundCallNotReceived:
      return "Voice Mail";
    default:
      return "Waiting";
  }
}

function getTargetCall(
  calls: AudioConferenceCalls,
  initiatedNumber: string
): AudioConferenceCall | undefined {
  const matchingCalls = (calls || []).filter((call) => call.calledNumber === initiatedNumber);

  if (!matchingCalls.length) {
    return undefined;
  }

  const activeMatch = matchingCalls.find(
    (call) => !completedStatuses.has(call.status?.current as CallStatusType)
  );

  if (activeMatch) {
    return activeMatch;
  }

  return matchingCalls
    .slice()
    .sort((a, b) => (b.lastUpdatedAt || "").localeCompare(a.lastUpdatedAt || ""))[0];
}

export function ModalOutboundDtmfDial({
  show,
  handleShow,
  callSid,
  audioConferenceCalls,
  onCallConferenceParticipant,
  onSendOutboundDtmf,
}: Props) {
  const dispatch = useAppDispatch();
  const [phase, setPhase] = useState<"dial" | "dtmf">("dial");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [initiatedNumber, setInitiatedNumber] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [legCallSidInput, setLegCallSidInput] = useState("");
  const [lastAutoPopulatedLegCallSid, setLastAutoPopulatedLegCallSid] = useState("");
  const [autoSentLength, setAutoSentLength] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [lastSentDigits, setLastSentDigits] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const targetCall = useMemo(
    () => (initiatedNumber ? getTargetCall(audioConferenceCalls, initiatedNumber) : undefined),
    [audioConferenceCalls, initiatedNumber]
  );

  const canSendDigits =
    !!callSid &&
    !!targetCall?.callSid &&
    targetCall.status?.current === CallStatusType.OutboundCallReceived;

  useEffect(() => {
    if (!show) {
      setPhase("dial");
      setPhoneNumberInput("");
      setPhoneError("");
      setInitiatedNumber("");
      setCodeInput("");
      setLegCallSidInput("");
      setLastAutoPopulatedLegCallSid("");
      setAutoSentLength(0);
      setIsSending(false);
      setLastSentDigits("");
      setFeedbackMessage("");
    }
  }, [show]);

  useEffect(() => {
    const nextLegCallSid = targetCall?.callSid ?? "";

    if (!nextLegCallSid) {
      return;
    }

    if (!legCallSidInput || legCallSidInput === lastAutoPopulatedLegCallSid) {
      setLegCallSidInput(nextLegCallSid);
      setLastAutoPopulatedLegCallSid(nextLegCallSid);
    }
  }, [lastAutoPopulatedLegCallSid, legCallSidInput, targetCall?.callSid]);

  useEffect(() => {
    setAutoSentLength((prev) => Math.min(prev, codeInput.length));
  }, [codeInput.length]);

  useEffect(() => {
    if (
      !show ||
      phase !== "dtmf" ||
      codeInput.length <= autoSentLength ||
      !legCallSidInput ||
      !callSid ||
      !canSendDigits ||
      isSending
    ) {
      return;
    }

    const digitsToSend = codeInput.slice(autoSentLength);
    const nextAutoSentLength = codeInput.length;
    const timer = window.setTimeout(async () => {
      setIsSending(true);
      setFeedbackMessage(`Sending ${digitsToSend}`);

      try {
        await onSendOutboundDtmf({
          callSid,
          legCallSid: legCallSidInput,
          digits: digitsToSend,
        });

        setLastSentDigits(digitsToSend);
        setFeedbackMessage(`Sent ${digitsToSend}`);
        setAutoSentLength(nextAutoSentLength);
      } catch (error) {
        console.error(error);
        setFeedbackMessage(`Failed to send ${digitsToSend}`);
      } finally {
        setIsSending(false);
      }
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    callSid,
    canSendDigits,
    codeInput,
    autoSentLength,
    isSending,
    legCallSidInput,
    onSendOutboundDtmf,
    phase,
    show,
  ]);

  const initiateOutboundCall = async () => {
    if (!callSid) {
      setPhoneError("Parent call SID is missing.");
      return;
    }

    if (!isValidUSPhoneNumber(phoneNumberInput)) {
      setPhoneError("Invalid US phone number");
      return;
    }

    const normalizedNumber = normalizeUSPhoneNumber(phoneNumberInput);
    const activeExistingCall = (audioConferenceCalls || []).find(
      (call) =>
        call.calledNumber === normalizedNumber &&
        !completedStatuses.has(call.status?.current as CallStatusType)
    );

    if (activeExistingCall) {
      setPhoneError(
        `The number ${formatUSPhoneNumber(normalizedNumber) || normalizedNumber} is already in a call`
      );
      return;
    }

    setPhoneError("");
    setFeedbackMessage("Calling outbound number...");
    setInitiatedNumber(normalizedNumber);
    setPhase("dtmf");
    setCodeInput("");
    setAutoSentLength(0);
    setLastSentDigits("");

    dispatch(
      ActionAudioCall.UpsertAudioConferenceCall({
        calledNumber: normalizedNumber,
        status: {
          current: CallStatusType.OutboundCallInitiated,
        },
      })
    );

    await onCallConferenceParticipant({
      phoneNumber: phoneNumberInput,
      callSid,
    });
  };

  const appendKey = (digit: string) => {
    if (phase === "dial") {
      setPhoneError("");
      setPhoneNumberInput((prev) => prev + digit);
      return;
    }

    setFeedbackMessage("");
    setCodeInput((prev) => prev + digit);
  };

  const removeLastKey = () => {
    if (phase === "dial") {
      setPhoneError("");
      setPhoneNumberInput((prev) => prev.slice(0, -1));
      return;
    }

    setFeedbackMessage("");
    setCodeInput((prev) => prev.slice(0, -1));
  };

  const triggerApiManually = async () => {
    if (!callSid) {
      setFeedbackMessage("Parent call SID is missing.");
      return;
    }

    if (!legCallSidInput) {
      setFeedbackMessage("legCallSid is required.");
      return;
    }

    if (!codeInput) {
      setFeedbackMessage("Code input is required.");
      return;
    }

    setIsSending(true);
    setFeedbackMessage(`Triggering API with ${codeInput}`);

    try {
      await onSendOutboundDtmf({
        callSid,
        legCallSid: legCallSidInput,
        digits: codeInput,
      });
      setLastSentDigits(codeInput);
      setFeedbackMessage(`Triggered API with ${codeInput}`);
    } catch (error) {
      console.error(error);
      setFeedbackMessage(`Failed to trigger API with ${codeInput}`);
    } finally {
      setIsSending(false);
    }
  };

  const callStatusLabel = getCallStatusLabel(targetCall?.status?.current, targetCall?.status?.prev);
  const targetNumberLabel =
    formatUSPhoneNumber(initiatedNumber || targetCall?.calledNumber || "") ||
    initiatedNumber ||
    targetCall?.calledNumber ||
    "";

  return (
    <Modal show={show} onHide={() => handleShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>{phase === "dial" ? "Call DTMF" : "Call DTMF - Zoom IVR"}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="dialer-modal-body">
        <div className="dialer-modal">
          <div className="d-flex flex-column justify-content-center align-items-center dialer-content">
            <div
              className="w-100 mb-3"
              style={{ maxWidth: "320px" }}
            >
              <div className="d-flex align-items-end mb-2" style={{ gap: "8px" }}>
                <div style={{ flex: "1 1 0" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    callSid
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={callSid ?? ""}
                    readOnly
                    style={{ height: "32px" }}
                  />
                </div>
                <div style={{ flex: "1 1 0" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    Code
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={codeInput}
                    onChange={(event) => {
                      setFeedbackMessage("");
                      setCodeInput(event.target.value);
                    }}
                    placeholder="Code"
                    style={{ height: "32px" }}
                  />
                </div>
              </div>
              <div className="d-flex align-items-end" style={{ gap: "8px" }}>
                <div style={{ flex: "1 1 auto" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    legCallSid
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={legCallSidInput}
                    onChange={(event) => {
                      setFeedbackMessage("");
                      setLegCallSidInput(event.target.value);
                    }}
                    placeholder="Editable leg call SID"
                    style={{ height: "32px" }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={triggerApiManually}
                  disabled={isSending}
                  style={{ height: "32px", minWidth: "104px", padding: "0 12px", whiteSpace: "nowrap" }}
                >
                  Trigger API
                </button>
              </div>
            </div>

            <div className="d-flex flex-row" style={{ marginRight: "-50px" }}>
              <div
                className="dialer-output shadow-sm"
                style={{
                  minWidth:
                    (phase === "dial" ? phoneNumberInput.length : codeInput.length) > 25
                      ? (phase === "dial" ? phoneNumberInput.length : codeInput.length) * 10 + "px"
                      : "250px",
                }}
              >
                <input
                  className="border form-control text-center"
                  value={phase === "dial" ? phoneNumberInput : codeInput}
                  onChange={(event) => {
                    const allowed = event.target.value.replace(/[^\d*#]/g, "");
                    if (phase === "dial") {
                      setPhoneError("");
                      setPhoneNumberInput(allowed);
                      return;
                    }
                    setFeedbackMessage("");
                    setCodeInput(allowed);
                  }}
                  placeholder={phase === "dial" ? "Enter Zoom number" : "Enter Zoom code"}
                  style={{
                    minWidth:
                      (phase === "dial" ? phoneNumberInput.length : codeInput.length) > 25
                        ? (phase === "dial" ? phoneNumberInput.length : codeInput.length) * 10 + "px"
                        : "250px",
                  }}
                />
              </div>
              <button
                type="button"
                className="btn dial-backspace shadow-sm ripple"
                onClick={removeLastKey}
                disabled={phase === "dial" ? !phoneNumberInput : !codeInput}
              >
                <FontAwesomeIcon icon={faDeleteLeft} className="mr-1 ml-1" />
              </button>
            </div>

            <div className="key-row-wrapper mt-3">
              {keypadRows.map((row) => (
                <div className="key-row" key={row.join("-")}>
                  {row.map((digit) => (
                    <button
                      key={digit}
                      value={digit}
                      className="btn btn-light shadow-sm ripple"
                      type="button"
                      onClick={() => appendKey(digit)}
                    >
                      {digit}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {phase === "dial" && (
              <>
                <div className="text-center mt-3" style={{ maxWidth: "260px", fontSize: "12px", color: "#6c757d" }}>
                  Start a new outbound call first. Once Zoom answers, this dialer switches to DTMF mode.
                </div>
                {phoneError && (
                  <div className="text-center mt-2" style={{ maxWidth: "260px", fontSize: "12px", color: "#dc3545" }}>
                    {phoneError}
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-success mt-3"
                  onClick={initiateOutboundCall}
                >
                  Call Number
                </button>
              </>
            )}

            {phase === "dtmf" && (
              <div className="text-center mt-3" style={{ maxWidth: "260px", fontSize: "12px" }}>
                <div style={{ fontWeight: 600 }}>{targetNumberLabel || "Outbound call"}</div>
                <div style={{ color: canSendDigits ? "#198754" : "#fd7e14" }}>{callStatusLabel}</div>
                <div className="mt-2" style={{ color: "#6c757d" }}>
                  Digits send automatically 1.5 seconds after typing stops.
                </div>
                {!callSid && <div style={{ color: "#dc3545" }}>Parent call SID is missing.</div>}
                {!legCallSidInput && targetCall && !targetCall.callSid && (
                  <div style={{ color: "#fd7e14" }}>Waiting for outbound leg SID.</div>
                )}
                {!targetCall && (
                  <div style={{ color: "#fd7e14" }}>Waiting for outbound call to appear.</div>
                )}
                {targetCall && !canSendDigits && targetCall.status?.current !== CallStatusType.OutboundCallReceived && (
                  <div style={{ color: "#fd7e14" }}>
                    Digits will stay buffered until the outbound call is connected.
                  </div>
                )}
                {isSending && <div style={{ color: "#0d6efd" }}>Sending digits...</div>}
                {!isSending && feedbackMessage && <div>{feedbackMessage}</div>}
                {!isSending && lastSentDigits && <div>Last sent: {lastSentDigits}</div>}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
